import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf, fmtDate, urgency, daysUntil } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Upload, ArrowRight, CheckCircle2, Loader2, BookOpen, Flame } from "lucide-react";
import { computeStreak } from "@/lib/streak";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, e] = await Promise.all([api.get("/courses"), api.get("/events")]);
      setCourses(c.data);
      setEvents(e.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const courseById = Object.fromEntries(courses.map((c) => [c.course_id, c]));

  const upcoming = events
    .filter((e) => e.status !== "completed" && e.date && daysUntil(e.date) !== null && daysUntil(e.date) >= 0)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 6);

  const total = events.length;
  const done = events.filter((e) => e.status === "completed").length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const thisWeek = events.filter((e) => {
    const n = daysUntil(e.date);
    return n !== null && n >= 0 && n <= 7 && e.status !== "completed";
  });

  const { streak, thisWeek: doneThisWeek } = computeStreak(events);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  if (courses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20" data-testid="dashboard-empty">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 mb-5">
          <BookOpen className="h-8 w-8" />
        </span>
        <h1 className="text-2xl font-semibold text-slate-900">Your semester starts here.</h1>
        <p className="mt-2 text-slate-500 max-w-sm">Upload your first syllabus and Syllabify will organize your assignments, exams, and deadlines.</p>
        <Button onClick={() => navigate("/upload")} data-testid="empty-upload-btn"
          className="mt-6 bg-blue-600 hover:bg-blue-700 rounded-lg px-6 py-5">
          <Upload className="h-4 w-4 mr-1" /> Upload a Syllabus
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">{greeting()}, {user?.name?.split(" ")[0]}.</h1>
          <p className="mt-1 text-slate-500">Here's what's coming up this semester.</p>
        </div>
        <Button onClick={() => navigate("/upload")} data-testid="dashboard-upload-btn"
          className="bg-slate-900 hover:bg-slate-800 rounded-lg self-start">
          <Upload className="h-4 w-4 mr-1" /> Upload syllabus
        </Button>
      </div>

      {/* Streak */}
      <section data-testid="streak-card" className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-5 flex items-center gap-4">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
          <Flame className="h-6 w-6" />
        </span>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900" data-testid="streak-count">
            {streak > 0 ? `${streak}-week streak` : "Start your streak"}
          </p>
          <p className="text-xs text-slate-500">
            {streak > 0
              ? `You've checked off work ${streak} week${streak === 1 ? "" : "s"} in a row. ${doneThisWeek} done this week.`
              : "Complete an assignment this week to begin a streak."}
          </p>
        </div>
      </section>

      {/* Upcoming */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3">Upcoming</h2>
        {upcoming.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="no-upcoming">
            You're all caught up! 🎉
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {upcoming.map((e) => {
              const c = courseById[e.course_id];
              const col = colorOf(c?.color);
              const u = urgency(e.date, e.status);
              return (
                <div key={e.event_id} data-testid={`upcoming-${e.event_id}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{e.title}</p>
                    <p className="text-xs text-slate-500">{c?.code || c?.name} · {fmtDate(e.date)}</p>
                  </div>
                  <span className={`text-xs font-semibold ${u.color}`}>{u.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        {/* This week */}
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">This week</h2>
          {thisWeek.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing due in the next 7 days.</p>
          ) : (
            <ul className="space-y-3">
              {thisWeek.slice(0, 5).map((e) => (
                <li key={e.event_id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate">{e.title}</span>
                  <span className="text-slate-400 shrink-0 ml-3">{fmtDate(e.date)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Progress */}
        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Progress</h2>
          {total === 0 ? (
            <p className="text-sm text-slate-500">Complete your first task to see your progress.</p>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <span className="text-slate-900 font-medium">{done} of {total} tasks completed</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <button onClick={() => navigate("/dashboard/assignments")} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                View all assignments <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
